var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { getI18n, I18nContext, getDefaults } from './context';
import { hasLoadedNamespace, loadNamespaces } from './utils';
export function useTranslation(ns, props) {
    if (props === void 0) { props = {}; }
    var i18nFromProps = props.i18n;
    var i18nFromContext = (React.useContext(I18nContext) || {}).i18n;
    var i18n = i18nFromProps || i18nFromContext || getI18n() || undefined;
    if (!i18n) {
        console.warn('You will need to pass in an @ies/starling_intl instance by using registerReactInstance');
        var notReadyT = function (k) { return (Array.isArray(k) ? k[k.length - 1] : k); };
        var retNotReady = [notReadyT, {}, false];
        retNotReady.t = notReadyT;
        retNotReady.i18n = {};
        retNotReady.ready = false;
        return retNotReady;
    }
    var coreInstance = i18n.i18nInstance.instance;
    if (coreInstance.options.react && coreInstance.options.react.wait !== undefined) {
        console.warn('It seems you are still using the old wait option, you may migrate to the new useSuspense behavior.');
    }
    var i18nOptions = __assign(__assign(__assign({}, getDefaults()), coreInstance.options.react), props);
    var useSuspense = i18nOptions.useSuspense;
    var namespaces = ns || (coreInstance.options && coreInstance.options.defaultNS) || [];
    namespaces = typeof namespaces === 'string' ? [namespaces] : namespaces || ['translation'];
    var ready = (coreInstance.isInitialized || coreInstance.initializedStoreOnce) &&
        namespaces.every(function (n) { return hasLoadedNamespace(n, coreInstance, i18nOptions); });
    function getT() {
        return {
            // t: coreInstance.getFixedT(null, i18nOptions.nsMode === 'fallback' ? namespaces : namespaces[0]),
            // todo:: get fixed t
            t: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                if (!Array.isArray(args))
                    return '';
                var keys = args[0], _a = args[1], options = _a === void 0 ? {} : _a, fallbackText = args[2];
                if (Array.isArray(namespaces) && !!namespaces.filter(function (i) { return !!i; }).length) {
                    options.ns = namespaces;
                }
                return i18n.t(keys, options, fallbackText);
            }
        };
    }
    var _a = React.useState(getT()), t = _a[0], setT = _a[1];
    var isMounted = React.useRef(true);
    React.useEffect(function () {
        var bindI18n = i18nOptions.bindI18n, bindI18nStore = i18nOptions.bindI18nStore;
        isMounted.current = true;
        // if not ready and not using suspense load the namespaces
        // in side effect and do not call resetT if unmounted
        if (!ready && !useSuspense) {
            loadNamespaces(coreInstance, namespaces, function () {
                if (isMounted.current)
                    setT(getT());
            });
        }
        function boundReset() {
            if (isMounted.current)
                setT(getT());
        }
        var bindI18nEvents = !!bindI18n ? bindI18n.split(' ').filter(function (i) { return !!i; }) : '';
        var bindI18nStoreEvents = !!bindI18nStore
            ? bindI18nStore.split(' ').filter(function (i) { return !!i; })
            : '';
        // bind events to trigger change, like languageChanged
        if (bindI18nEvents && coreInstance) {
            bindI18nEvents.forEach(function (e) { return coreInstance.on(e, boundReset); });
        }
        if (bindI18nStoreEvents && coreInstance) {
            // @ts-ignore
            bindI18nStoreEvents.forEach(function (e) { return coreInstance.store.on(e, boundReset); });
        }
        // unbinding on unmount
        return function () {
            isMounted.current = false;
            if (bindI18nEvents && coreInstance)
                bindI18nEvents.forEach(function (e) { return coreInstance.off(e, boundReset); });
            if (bindI18nStoreEvents && coreInstance)
                // @ts-ignore
                bindI18nStoreEvents.forEach(function (e) { return coreInstance.store.off(e, boundReset); });
        };
    }, [coreInstance, namespaces.join()]); // re-run effect whenever list of namespaces changes
    // t is correctly initialized by useState hook. We only need to update it after i18n
    // instance was replaced (for example in the provider).
    var isInitial = React.useRef(true);
    React.useEffect(function () {
        if (isMounted.current && !isInitial.current) {
            setT(getT());
        }
        isInitial.current = false;
    }, [coreInstance]); // re-run when i18n instance was replaced
    var ret = [t.t, i18n, ready];
    ret.t = t.t;
    ret.i18n = i18n;
    ret.ready = ready;
    // return hook stuff if ready
    if (ready)
        return ret;
    // not yet loaded namespaces -> load them -> and return if useSuspense option set false
    if (!ready && !useSuspense)
        return ret;
    // not yet loaded namespaces -> load them -> and trigger suspense
    throw new Promise(function (resolve) {
        loadNamespaces(coreInstance, namespaces, function () {
            resolve();
        });
    });
}
export default useTranslation;
//# sourceMappingURL=useTranslation.js.map